import type { MetaFunction } from 'react-router'
import { Link, useLoaderData } from 'react-router'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { BreadcrumbSchema } from '~/components/structured-data'
import { STATIC_PAGE_HEADERS } from '~/lib/cache.server'
import { ArrowRight, BookOpen, Award, TrendingUp } from 'lucide-react'
import { VoirAussi, CROSS_LINKS } from '~/components/voir-aussi'

export function headers() { return STATIC_PAGE_HEADERS }

export async function loader() {
  const { METIERS } = await import('~/lib/metiers-data.server')
  return {
    items: METIERS.map((m) => ({
      slug: m.slug,
      artisan: m.artisan,
      shortDesc: m.shortDesc,
      salaireDebutant: m.salaire.debutant,
      firstFormation: m.formations[0]?.name ?? '',
    })),
  }
}

export const meta: MetaFunction = () => [
  { title: "Les métiers de l'artisanat — formations, salaires, qualifications | AfficheServices" },
  { name: 'description', content: "Panorama complet des métiers du bâtiment : formations, salaires, certifications RGE, perspectives d'évolution. Plombier, électricien, menuisier, couvreur, maçon et plus." },
  { tagName: 'link', rel: 'canonical', href: 'https://www.afficheservices.fr/metier' },
  { property: 'og:title', content: "Les métiers de l'artisanat — formations, salaires, qualifications | AfficheServices" },
  { property: 'og:description', content: "Panorama complet des métiers du bâtiment : formations, salaires, certifications RGE, perspectives d'évolution." },
  { property: 'og:url', content: 'https://www.afficheservices.fr/metier' },
]

export default function MetiersHubPage() {
  const { items } = useLoaderData<typeof loader>()
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <BreadcrumbSchema items={[
        { name: 'Accueil', url: 'https://www.afficheservices.fr/' },
        { name: 'Métiers', url: 'https://www.afficheservices.fr/metier' },
      ]} />

      <section className="bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-900 px-4 py-14 text-white sm:py-20">
        <div className="mx-auto max-w-4xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-medium text-indigo-200">
            <BookOpen className="h-3.5 w-3.5" /> Fiches métiers 2026
          </div>
          <h1 className="mt-5 text-3xl font-extrabold sm:text-5xl">
            Les métiers de l'artisanat
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-indigo-200">
            Formations, salaires, qualifications RGE, évolution de carrière.
            Tout ce qu'il faut savoir sur {items.length} métiers du bâtiment.
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 lg:px-6">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((m) => (
            <Link
              key={m.slug}
              to={`/metier/${m.slug}`}
              className="group rounded-2xl border border-border bg-white p-6 shadow-xs transition-all hover:border-primary hover:shadow-md"
            >
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-slate-900 group-hover:text-primary">
                  {m.artisan.charAt(0).toUpperCase() + m.artisan.slice(1)}
                </h2>
                <ArrowRight className="h-4 w-4 text-slate-300 group-hover:text-primary" />
              </div>
              <p className="mt-2 text-xs text-slate-500 line-clamp-3">{m.shortDesc}</p>
              <dl className="mt-4 space-y-2 text-xs">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-3 w-3 text-emerald-500" />
                  <dt className="text-slate-500">Salaire débutant :</dt>
                  <dd className="font-semibold text-slate-900">{m.salaireDebutant}</dd>
                </div>
                <div className="flex items-center gap-2">
                  <Award className="h-3 w-3 text-primary" />
                  <dt className="text-slate-500">Formation :</dt>
                  <dd className="font-semibold text-slate-900">{m.firstFormation.split(' ').slice(0, 4).join(' ')}…</dd>
                </div>
              </dl>
            </Link>
          ))}
        </div>

        <VoirAussi links={CROSS_LINKS.services} />

        <section className="mt-16 rounded-2xl border border-border bg-slate-50 p-6 text-center lg:p-10">
          <h2 className="text-xl font-bold text-slate-900">Vous êtes un particulier ?</h2>
          <p className="mt-2 text-sm text-slate-600">
            Trouvez un artisan qualifié près de chez vous et recevez 3 devis gratuits.
          </p>
          <Link
            to="/devis-gratuit"
            className="mt-5 inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-bold text-white hover:bg-primary-hover"
          >
            Demander mes devis gratuits <ArrowRight className="h-4 w-4" />
          </Link>
        </section>
      </div>

      <Footer />
    </div>
  )
}
